import type { ReactNode } from "react";

/**
 * Plain-language glossary for the few abbreviations the main screen keeps
 * (standard, widely printed units). Everything else is spelled out in plain
 * words instead (design review 2026-09: TRAFFIC, not iTIC; REPORTS, not CR).
 */
export const GLOSSARY = {
  AQI: "Air Quality Index (US EPA scale). 0–50 good · 51–100 moderate · above 100 unhealthy for sensitive groups",
  "PM2.5": "Fine dust particles (≤ 2.5 µm) — the main Bangkok air-pollution measure, in µg/m³",
  SIM: "Simulated — modeled from the published timetable, not live GPS",
} as const;

export type GlossaryKey = keyof typeof GLOSSARY;

interface Props {
  /** Glossary key; the explanation comes from GLOSSARY. */
  k: GlossaryKey;
  /** Visible text — defaults to the key itself. */
  children?: ReactNode;
}

/**
 * An abbreviation with its explanation reachable by mouse (title) AND
 * keyboard (focusable, with a CSS tooltip on :focus-visible — see
 * `.term[data-tip]` in layout.css). Screen readers get "TERM — explanation"
 * through aria-label, since <abbr title> is announced inconsistently.
 */
export function Term({ k, children }: Props) {
  const hint = GLOSSARY[k];
  const visible = typeof children === "string" ? children : k;
  return (
    <abbr className="term" title={hint} data-tip={hint} tabIndex={0} aria-label={`${visible} — ${hint}`}>
      {children ?? k}
    </abbr>
  );
}
